import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentUserPermissions } from '@app/services/business';
import { BusinessMemberPermissions, PermissionKey } from '@app/models/business';
import { useAppSelector } from '@app/redux/hooks';

interface PermissionContextType {
  isOwner: boolean;
  permissions: BusinessMemberPermissions | null;
  isLoading: boolean;
  can: (key: PermissionKey) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const businessId = useAppSelector((state) => state.business.selected?.id);

  const { data, isLoading } = useQuery({
    queryKey: ['permissions', businessId],
    queryFn: () => getCurrentUserPermissions(),
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });

  const value = useMemo<PermissionContextType>(() => {
    const isOwner = data?.isOwner ?? false;
    const permissions = data?.permissions ?? null;

    return {
      isOwner,
      permissions,
      isLoading,
      can: (key: PermissionKey) => {
        if (isOwner) return true;
        return permissions?.[key] === true;
      },
    };
  }, [data, isLoading]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
